import { levelProgress } from '@thicket/core';
import { html, nothing, render } from 'lit-html';
import { HttpError, request } from './api.js';
import { SandboxStore } from './dev/sandbox.js';
import { labPreview, labReady, tabIcon } from './garden/art/lab/index.js';
import { icon } from './garden/art/index.js';
import './styles.css';
import { app, onRender, storeDeps, update, type Tab } from './ui/app.js';
import { devBar } from './ui/dev.js';
import { bloomSheet, gardenView, setImmersive } from './ui/garden.js';
import { journalView } from './ui/journal.js';
import { needLab, startLab, startLabWhenIdle } from './ui/labLoad.js';
import { settingsView } from './ui/settings.js';
import { choosingStarter, starterView } from './ui/starter.js';
import { taskSheet } from './ui/taskSheet.js';
import { taskCounts, tasksView } from './ui/today.js';
import { moreView } from './ui/more.js';
import { maybeStartTour, tourNav } from './ui/tour.js';

const root = document.getElementById('app')!;
root.replaceChildren();
/** Dev mode lives under /dev: a sandbox copy of the garden with the Lab's art. */
const DEV = location.pathname === '/dev' || location.pathname.startsWith('/dev/');
const base = DEV ? '/dev' : '';
/** The bottom bar; Settings lives under More. */
const TABS: { id: Tab; label: string; ico: string }[] = [
  { id: 'habits', label: 'Habits', ico: '±' },
  { id: 'dailies', label: 'Dailies', ico: '✓' },
  { id: 'todos', label: 'To-dos', ico: '❀' },
  { id: 'garden', label: 'Garden', ico: '♣' },
  { id: 'journal', label: 'Field Journal', ico: '❧' },
  { id: 'more', label: 'More', ico: '⋯' },
];
const PAGES: Tab[] = [...TABS.map((t) => t.id), 'settings'];
const TITLES: Record<Tab, string> = {
  habits: 'Habits', dailies: 'Dailies', todos: 'To-dos', garden: 'Garden', more: 'More', journal: 'Field Journal', settings: 'Settings',
};
/** What the round + in the header adds on each task tab. */
const ADDS: Partial<Record<Tab, { type: 'habit' | 'daily' | 'todo'; label: string }>> = {
  habits: { type: 'habit', label: 'Add a habit' }, dailies: { type: 'daily', label: 'Add a daily' }, todos: { type: 'todo', label: 'Add a to-do' },
};
/** Which bottom tab lights up for a page. */
const tabOf = (t: Tab): Tab => (t === 'settings' ? 'more' : t);

function go(tab: Tab) {
  update(() => {
    app.tab = tab;
    app.planting = null;
  });
  history.replaceState(null, '', tab === 'dailies' ? base || '/' : `${base}/${tab}`);
  window.scrollTo(0, 0);
}

function view() {
  const store = app.store;
  const s = store.view;
  if (!s) return html`<p style="padding:24px">${DEV ? 'Loading dev mode and the Lab art…' : 'Loading your garden…'}</p>`;
  const lp = levelProgress(s.profile.xp, store.rules);
  const status = store.offline
    ? `Offline${store.queue.length ? ` · ${store.queue.length} to sync` : ''}`
    : store.queue.length ? 'Saving…' : '';
  // The starting place chooser and the garden can't show without the Lab art: start it now.
  if (choosingStarter(s) || app.tab === 'garden') needLab();
  if (choosingStarter(s)) return html`${devBar()}${starterView(s)}<div class="toasts" aria-live="polite">${app.toasts.map((t) => html`<div class="toast">${t.text}</div>`)}</div>`;
  const full = app.tab === 'garden';
  if (!full) app.immersive = false;
  const tab = app.tab;
  const counts = taskCounts(s);
  const add = ADDS[tab];
  const sub = tab === 'settings';
  const lvl = html`<span class="pill">Lvl ${lp.level}</span>`;
  const coins = html`<span class="pill"><span class="coin" aria-hidden="true"></span>${s.profile.coins}<span class="sr">coins</span></span>`;
  const art = labReady() ? labPreview(s.profile.starterBiome, s) : null;
  return html`<div class=${`shell${full ? ' full' : ''}${full && app.immersive ? ' immersive' : ''}`}>
    ${devBar()}
    <header class="hud">
      <div class="title-row">
        ${sub ? html`<button class="round" aria-label="Back to More" @click=${() => go('more')}>‹</button>` : full ? lvl : html`<span class="round-spacer"></span>`}
        <h1>${TITLES[tab]}</h1>
        ${add ? html`<button class="round fab" aria-label=${add.label} @click=${() => update(() => (app.sheet = { kind: 'task', taskId: null, newType: add.type }))}>+</button>`
          : full ? coins : html`<span class="round-spacer"></span>`}
      </div>
      ${add ? html`<div class="stats">
        <button class="stats-art" aria-label="Open your garden" @click=${() => go('garden')}>${art ? html`<img src=${art} alt="">` : nothing}</button>
        <div class="stats-bars">
          <div class="bar xp" role="progressbar" aria-label="Experience" aria-valuenow=${lp.into} aria-valuemax=${lp.needed}><i style=${`width:${(lp.into / lp.needed) * 100}%`}></i></div>
          <div class="bar-label xp"><b>XP</b><span>${lp.into} / ${lp.needed}</span></div>
          <div class="bar today" role="progressbar" aria-label="Dailies done today" aria-valuenow=${counts.done} aria-valuemax=${counts.due}><i style=${`width:${counts.due ? (counts.done / counts.due) * 100 : 0}%`}></i></div>
          <div class="bar-label today"><b>Today</b><span>${counts.due ? `${counts.done} / ${counts.due} dailies` : 'Nothing due'}</span></div>
        </div>
        <div class="stats-pills">${lvl}<span class="sync" role="status">${status}</span>${coins}</div>
      </div>` : status ? html`<p class="sync" role="status">${status}</p>` : nothing}
    </header>
    ${full ? html`<main class="garden-main">${gardenView(s)}</main>` : html`<main class="wrap">
      ${tab === 'journal' ? journalView(s) : tab === 'settings' ? settingsView(s) : tab === 'more' ? moreView(s) : tasksView(s, tab as 'habits' | 'dailies' | 'todos')}
    </main>`}
    <nav class="tabs" aria-label="Sections"><div class="inner">${TABS.map((t) => {
      const badge = t.id === 'dailies' ? counts.due - counts.done : t.id === 'todos' ? counts.todos : 0;
      return html`<button data-tab=${t.id} aria-label=${t.label} aria-current=${tabOf(tab) === t.id ? 'page' : 'false'} @click=${() => go(t.id)}>
        ${t.id === 'more' ? html`<span class="more-ico" aria-hidden="true"><i></i><i></i><i></i></span>`
          : labReady() ? html`<img class="ico" src=${icon(`tab:${t.id}`, (ctx) => tabIcon(ctx, t.id as 'habits'), 24, 24)} alt="" width="28" height="28">`
          : html`<span class="ico" aria-hidden="true">${t.ico}</span>`}
        ${badge > 0 ? html`<span class="badge" aria-hidden="true">${badge}</span>` : nothing}
      </button>`;
    })}</div></nav>
    </div>
    ${app.sheet?.kind === 'task' ? taskSheet(s, app.sheet.taskId, app.sheet.newType) : app.sheet?.kind === 'bloom' ? bloomSheet(s, app.sheet.plantId) : nothing}
    <div class="toasts" aria-live="polite">${app.toasts.map((t) => html`<div class="toast">${t.text}</div>`)}</div>
  `;
}

tourNav(go);
onRender(() => render(view(), root));
render(view(), root);

const initial = location.pathname.slice(base.length + 1) as Tab;
if (PAGES.includes(initial)) app.tab = initial;
else if ((initial as string) === 'today') app.tab = 'dailies';

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (app.sheet || app.planting) update(() => { app.sheet = null; app.planting = null; });
  // Leaves the browser's full screen as well, so the two never disagree.
  else if (app.immersive) setImmersive(false);
});

/** Another device (or Claude) saved: the server says so over a WebSocket, and we refetch. */
function connectLive(delay = 1000) {
  const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/live`);
  ws.onmessage = (e) => {
    try {
      const msg = JSON.parse(e.data as string);
      if (msg.type === 'version') app.store.onRemoteVersion(msg.version);
    } catch {
      // Not ours.
    }
  };
  ws.onopen = () => (delay = 1000);
  ws.onclose = () => setTimeout(() => connectLive(Math.min(delay * 2, 60_000)), delay);
}

window.addEventListener('online', () => void app.store.flush());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void app.store.refresh();
});

const cantReach = () => render(html`<main class="wrap" style="padding-top:48px"><div class="box"><h2>Can't reach Thicket</h2><p>Check your connection and try again.</p>
    <button class="primary" @click=${() => location.reload()}>Try again</button></div></main>`, root);

async function startDev() {
  const me = await request<{ dev: boolean }>(storeDeps.fetch, '/api/me').catch((err) => {
    if (err instanceof HttpError && err.status === 401) return null;
    throw err;
  });
  if (!me) return storeDeps.onSignedOut();
  if (!me.dev) return location.replace('/');
  app.dev = app.canDev = true;
  app.store = new SandboxStore(storeDeps);
  app.store.subscribe(() => render(view(), root));
  await startLab();
  await app.store.load();
}

if (DEV) {
  startDev().catch((err) => {
    console.error(err);
    cantReach();
  });
} else {
  app.store.subscribe(() => render(view(), root));
  app.store.load().then(() => {
    // The task lists paint first; the garden art (a heavy page) starts once they have.
    startLabWhenIdle();
    connectLive();
    // A brand-new player picks a starting place first; the chooser starts the tour after.
    if (!choosingStarter(app.store.view!)) maybeStartTour();
    request<{ dev: boolean }>(storeDeps.fetch, '/api/me').then((me) => update(() => (app.canDev = me.dev)), () => {});
  }, cantReach);
}

// Built app only: the dev server's files change all the time and have no precache list.
if (import.meta.env.PROD && 'serviceWorker' in navigator && window.isSecureContext) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}
